import { supabase, Database } from './supabase';

type Activity = Database['public']['Tables']['activities']['Row'];
type Child = Database['public']['Tables']['children']['Row'];

interface AgeGroup {
  minAge: number;
  maxAge: number;
  maxDifficulty: number;
  activitiesPerSubject: number;
  description: string;
}

export const AGE_GROUPS: AgeGroup[] = [
  {
    minAge: 3,
    maxAge: 5,
    maxDifficulty: 1,
    activitiesPerSubject: 2,
    description: 'Early learners (3-5 years)'
  },
  {
    minAge: 6,
    maxAge: 8,
    maxDifficulty: 2,
    activitiesPerSubject: 3,
    description: 'Elementary learners (6-8 years)'
  },
  {
    minAge: 9,
    maxAge: 11,
    maxDifficulty: 3,
    activitiesPerSubject: 4,
    description: 'Advanced learners (9-11 years)'
  },
  {
    minAge: 12,
    maxAge: 18,
    maxDifficulty: 4,
    activitiesPerSubject: 5,
    description: 'Advanced learners (12+ years)'
  }
];

export const SUBJECTS = ['math', 'reading', 'science'] as const;

function getAgeGroup(age: number): AgeGroup {
  return AGE_GROUPS.find(group => age >= group.minAge && age <= group.maxAge) || AGE_GROUPS[AGE_GROUPS.length - 1];
}

export async function seedActivitiesForChild(child: Child): Promise<void> {
  try {
    console.log(`🌱 Seeding activities for ${child.name} (age ${child.age})`);
    
    const ageGroup = getAgeGroup(child.age);
    console.log(`📚 Using ${ageGroup.description} - max difficulty ${ageGroup.maxDifficulty}, ${ageGroup.activitiesPerSubject} per subject`);

    // Get recommended activities for the child's age
    const recommendedActivities = await getRecommendedActivitiesForAge(child.age);
    
    if (recommendedActivities.length === 0) {
      console.warn('No activities found for seeding');
      return;
    }

    // Log the activities that will be available to this child
    const activitiesBySubject = SUBJECTS.reduce((acc, subject) => {
      acc[subject] = recommendedActivities.filter(activity => activity.subject === subject);
      return acc;
    }, {} as Record<typeof SUBJECTS[number], Activity[]>);

    let totalActivities = 0;
    for (const subject of SUBJECTS) {
      const subjectActivities = activitiesBySubject[subject];
      console.log(`📖 ${subjectActivities.length} ${subject} activities available for ${child.name}`);
      totalActivities += subjectActivities.length;
    }

    console.log(`✅ Successfully prepared ${totalActivities} age-appropriate activities for ${child.name}`);
    console.log(`🎯 Child can now see activities with difficulty level ${ageGroup.maxDifficulty} and below`);
  } catch (error) {
    console.error('Failed to seed activities for child:', error);
    throw error;
  }
}

export async function getRecommendedActivitiesForAge(age: number): Promise<Activity[]> {
  const ageGroup = getAgeGroup(age);
  
  const { data: activities, error } = await supabase
    .from('activities')
    .select('*')
    .lte('difficulty_level', ageGroup.maxDifficulty)
    .order('difficulty_level', { ascending: true });

  if (error) {
    console.error('Error fetching recommended activities:', error);
    throw error;
  }

  // Return a balanced selection across subjects
  const result: Activity[] = [];
  
  for (const subject of SUBJECTS) {
    const subjectActivities = activities?.filter(a => a.subject === subject) || [];
    result.push(...subjectActivities.slice(0, ageGroup.activitiesPerSubject));
  }
  
  return result;
}